import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Switch } from "../../../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { Pencil } from "lucide-react";
import { Field, SectionCard } from "./shared";

const VOUCHER_TYPE_LABELS = {
  RECEIPT: "Receipt", PAYMENT: "Payment", CONTRA: "Contra", JOURNAL: "Journal",
  PURCHASE: "Purchase / Vendor Bill", CREDIT_NOTE: "Credit Note", DEBIT_NOTE: "Debit Note", ADJUSTMENT: "Adjustment",
};
// PURCHASE is deliberately excluded from this visible list - a Purchase /
// Vendor Bill is always numbered with the vendor's own invoice number, never
// this configurable sequence, so showing an editable Prefix/Next No./Preview
// row for it here would incorrectly suggest new bills use it. The backend
// still keeps a numbering config for PURCHASE internally, used only to
// format the automatic reversal entry if a vendor bill is cancelled.
const VOUCHER_TYPE_ORDER = ["RECEIPT", "PAYMENT", "CONTRA", "JOURNAL", "CREDIT_NOTE", "DEBIT_NOTE", "ADJUSTMENT"];

function formatPreview(prefix, fyCode, number, padding) {
  const p = Math.max(1, Math.min(10, Number(padding) || 6));
  const n = Math.max(1, Number(number) || 1);
  return `${(prefix || "").toUpperCase() || "???"}/${fyCode || "FY"}/${String(n).padStart(p, "0")}`;
}

/**
 * Voucher Numbering settings - /client/society/accounting/settings/voucher-numbering.
 *
 * Phase 5 correction (items 12-31): per-voucher-type Prefix / Starting
 * Number / Padding / reset-per-Financial-Year configuration, with a live
 * preview and a Last Issued / Next Number display computed by the backend
 * from the REAL counter state (never estimated here). The backend remains
 * the sole source of truth for the actual next number at post time - this
 * page only ever proposes a change, which the backend validates can never
 * reissue an already-used number before accepting it.
 */
export default function VoucherNumberingSettingsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // the row currently open in the edit dialog
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/society/accounting/engine/settings/voucher-numbering");
      const byType = Object.fromEntries((Array.isArray(res.data) ? res.data : []).map((r) => [r.voucherType, r]));
      setRows(VOUCHER_TYPE_ORDER.map((t) => byType[t]).filter(Boolean));
    } catch (err) {
      toast.error(formatErr(err, "Could not load Voucher Numbering settings"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (row) => {
    setEditing(row);
    setForm({ prefix: row.prefix, padding: row.padding, resetPerFY: row.resetPerFY, startNumber: row.startNumber });
  };

  const preview = useMemo(() => {
    if (!editing || !form) return "";
    // Preview the NEXT number this configuration would actually produce - if numbers have
    // already been issued this FY, that's laterHighest+1, not the raw Starting Number (a
    // Starting Number below what's already issued gets rejected by the backend on Save).
    const base = editing.hasIssuedNumbers && editing.nextNumber ? editing.nextNumber : null;
    if (base && Number(form.startNumber) === editing.startNumber && form.prefix === editing.prefix && Number(form.padding) === editing.padding) {
      return base;
    }
    return formatPreview(form.prefix, editing.currentFinancialYearCode, form.startNumber, form.padding);
  }, [editing, form]);

  const save = async () => {
    if (!editing || !form) return;
    try {
      setBusy(true);
      await api.patch(`/society/accounting/engine/settings/voucher-numbering/${editing.voucherType}`, form);
      toast.success(`${VOUCHER_TYPE_LABELS[editing.voucherType]} numbering updated`);
      setEditing(null);
      setForm(null);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update this voucher type's numbering"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Voucher Numbering</h2>
        <p className="mt-1 text-sm text-[#6B7280]">
          Configure the Prefix, Starting Number, zero-padding and Financial-Year reset behavior used to number each
          voucher type when it is Posted. Draft vouchers never consume a number - only Posting assigns one, atomically,
          so two users posting at the same time can never receive the same number.
        </p>
        <p className="mt-1 text-sm text-[#6B7280]">
          <strong>Purchase / Vendor Bill</strong> is not listed below - it is always numbered with the vendor's own
          invoice number (entered as "Vendor Invoice No." on the bill), never an auto-generated sequence. Cancelling a
          vendor bill still generates an internal reversal entry automatically; that alone does not need a
          configurable prefix here.
        </p>
      </div>

      <SectionCard title="Voucher Types">
        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead><TableHead>Prefix</TableHead><TableHead>Next No.</TableHead>
                  <TableHead>Padding</TableHead><TableHead>Reset FY</TableHead><TableHead>Last Issued</TableHead>
                  <TableHead>Preview</TableHead><TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.voucherType}>
                    <TableCell className="font-semibold text-[#111827]">{VOUCHER_TYPE_LABELS[row.voucherType]}</TableCell>
                    <TableCell className="font-mono">{row.prefix}</TableCell>
                    <TableCell className="font-mono text-xs">{row.nextNumber}</TableCell>
                    <TableCell>{row.padding}</TableCell>
                    <TableCell>{row.resetPerFY ? "Yes" : "No"}</TableCell>
                    <TableCell className="font-mono text-xs">{row.lastIssuedNumber || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{row.nextNumber}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openEdit(row)}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) { setEditing(null); setForm(null); } }}>
        <DialogContent>
          {editing && form && (
            <>
              <DialogHeader>
                <DialogTitle>Voucher Numbering — {VOUCHER_TYPE_LABELS[editing.voucherType]}</DialogTitle>
              </DialogHeader>

              <div className="space-y-3">
                <Field label="Prefix">
                  <Input
                    value={form.prefix}
                    onChange={(e) => setForm((f) => ({ ...f, prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8) }))}
                    placeholder="e.g. PMT"
                  />
                </Field>
                <Field label="Financial Year">
                  <Input value={editing.currentFinancialYearCode} disabled className="bg-[#F3F4F6] text-[#6B7280]" />
                </Field>
                <Field label="Starting Number">
                  <Input
                    type="number" min="1" value={form.startNumber}
                    onChange={(e) => setForm((f) => ({ ...f, startNumber: e.target.value }))}
                  />
                  {editing.hasIssuedNumbers && (
                    <p className="mt-1 text-xs text-amber-600">
                      Numbers have already been issued for this type (Last Issued: {editing.lastIssuedNumber || "n/a"}). A
                      Starting Number that would reuse an already-issued number is rejected on Save.
                    </p>
                  )}
                </Field>
                <Field label="Padding">
                  <Input
                    type="number" min="1" max="10" value={form.padding}
                    onChange={(e) => setForm((f) => ({ ...f, padding: e.target.value }))}
                  />
                </Field>
                <div className="flex items-center justify-between rounded-sm border border-[#E5E7EB] px-3 py-2">
                  <div>
                    <div className="text-sm font-semibold text-[#111827]">Reset sequence each Financial Year</div>
                    <div className="text-xs text-[#6B7280]">Recommended: enabled. When on, numbering restarts from the Starting Number every new Financial Year.</div>
                  </div>
                  <Switch checked={form.resetPerFY} onCheckedChange={(v) => setForm((f) => ({ ...f, resetPerFY: v }))} />
                </div>

                <div className="rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3">
                  <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Preview</div>
                  <div className="mt-1 font-mono text-base font-bold text-[#111827]">{preview}</div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" disabled={busy} onClick={() => { setEditing(null); setForm(null); }}>Cancel</Button>
                <Button disabled={busy} onClick={save} className="bg-[#111827] text-white hover:bg-[#374151]">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
